"use client";

import { useEffect } from "react";

/** 離脱確認メッセージ（beforeunload / リンククリック共通） */
const LEAVE_CONFIRM_MESSAGE = "入力項目がクリアされますが、よろしいですか？";

/** 戻る／意図的離脱時の確認メッセージ（showConfirm 用・HTML可） */
const LEAVE_CONFIRM_MESSAGE_NAVIGATE = "行った変更が保存されない可能性があります。<br/>このサイトを離れますか？";

/**
 * 入力途中離脱ガード用の共通フック。
 * フォームが dirty なときに beforeunload と同一オリジンリンククリックで確認を出し、
 * 意図的な遷移（次へ等）のときに確認を出さないようにする。
 *
 * @param {Object} options
 * @param {boolean} options.isFormDirty - フォームが初期値から変更されているか
 * @param {string} options.pathname - 現在の pathname（リンクが同一ページでないことを判定するため）
 * @param {Object} options.actions - showConfirm を持つアクション
 * @param {Object} options.router - Next.js の router（push 用）
 * @param {React.MutableRefObject<boolean>} options.allowBeforeUnloadRef - 意図的遷移時は false にする ref
 * @returns {{ allowNavigationAndRun: (callback: () => void | Promise<unknown>) => void | Promise<unknown> }}
 */
export function useFormLeaveGuard({ isFormDirty, pathname, actions, router, allowBeforeUnloadRef }) {
  // 入力途中で離脱する場合のアラート（beforeunload）
  useEffect(() => {
    if (!isFormDirty) return;
    const handleBeforeUnload = (e) => {
      if (allowBeforeUnloadRef.current) {
        e.preventDefault();
        e.returnValue = LEAVE_CONFIRM_MESSAGE;
        return LEAVE_CONFIRM_MESSAGE;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty, allowBeforeUnloadRef]);

  // レイアウト等のリンククリック時：クライアント遷移では beforeunload が発火しないため、クリックを傍受して確認してから遷移する
  useEffect(() => {
    if (!isFormDirty || !pathname) return;

    const handleClick = (e) => {
      if (!allowBeforeUnloadRef.current) return;
      const a = e.target.closest("a[href]");
      if (!a) return;
      if (a.target === "_blank") return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === pathname) return;
      } catch {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      actions.showConfirm(LEAVE_CONFIRM_MESSAGE, { color: "warning", icon: "warning" }).then((ok) => {
        if (ok) {
          allowBeforeUnloadRef.current = false;
          router.push(href);
        }
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [isFormDirty, pathname, actions, router, allowBeforeUnloadRef]);

  /**
   * 意図的な遷移（次へ押下など）の前に呼ぶ。beforeunload を出さないようにし、コールバック実行後に 1 秒で元に戻す。
   * @param {() => void | Promise<unknown>} callback - 遷移を伴う処理（例: formProps.irai() / formProps.syaryo(quote_number)）
   * @returns {void | Promise<unknown>} callback が Promise を返す場合はその Promise
   */
  function allowNavigationAndRun(callback) {
    allowBeforeUnloadRef.current = false;
    const result = callback();
    if (result != null && typeof result.then === "function") {
      result.finally(() => {
        setTimeout(() => {
          allowBeforeUnloadRef.current = true;
        }, 1000);
      });
      return result;
    }
    setTimeout(() => {
      allowBeforeUnloadRef.current = true;
    }, 1000);
    return result;
  }

  /**
   * 戻るボタンなどで離脱する前に呼ぶ。dirty なら確認ダイアログを出し、OK のときだけ callback を実行する。
   * @param {() => void} callback - 離脱時の処理（例: router.push(...)）
   * @returns {void | Promise<boolean>} dirty で確認を出した場合はその Promise
   */
  function confirmLeaveAndRun(callback) {
    if (!isFormDirty) {
      callback();
      return;
    }
    return actions.showConfirm(LEAVE_CONFIRM_MESSAGE_NAVIGATE, { color: "warning", icon: "warning" }).then((ok) => {
      if (ok) {
        allowBeforeUnloadRef.current = false;
        callback();
      }
    });
  }

  return { allowNavigationAndRun, confirmLeaveAndRun };
}
